import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StepContainer, NavigationButtons, InfoBox } from '../index';
import { useWizard } from '../../wizard';
import { contentApi } from '../../services/contentApi';
import { contentKeys } from '../../constants/contentKeys';
import { selfServiceApi } from '../../services/selfServiceApi';

const ConsentGivePermissionStep: React.FC = () => {
  const { selfServiceData, goBack, sessionId, flowId } = useWizard();
  const [title, setTitle] = useState<string>('Underskriv samtykke');
  const [body, setBody] = useState<string[]>([]);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    contentApi.getByKey(contentKeys.pas_samtykke.consent_give).then(c => {
      if (c?.title) {setTitle(c.title);}
      if (c?.body && c.body.length > 0) {setBody(c.body);}
    });
  }, []);

  const maskedCpr = useMemo(() => {
    const cpr = (selfServiceData.consentChildCpr || '').replace(/\D/g, '');
    if (cpr.length === 10) {
      return `${cpr.slice(0, 6)}-****`;
    }
    return undefined;
  }, [selfServiceData.consentChildCpr]);

  const roleText = useMemo(() => {
    switch (selfServiceData.consentCustodyType) {
      case 'med':
        return 'Samtykket skal underskrives af begge indehavere af forældremyndigheden.';
      case 'vaerge':
        return 'Samtykket skal underskrives af den registrerede værge.';
      case 'anden':
        return 'Samtykket skal underskrives af den bemyndigede person.';
      default:
        return 'Underskriv samtykket for at fortsætte.';
    }
  }, [selfServiceData.consentCustodyType]);

  const handleSign = useCallback(async () => {
    if (!sessionId) {
      return;
    }

    setRedirecting(true);
    try {
      const { signUrl } = await selfServiceApi.getSignUrl(sessionId, flowId);
      window.location.href = signUrl;
    } catch (error) {
      console.error('Failed to fetch sign URL', error);
      setRedirecting(false);
    }
  }, [flowId, sessionId]);

  useEffect(() => {
    const shouldAutoSign = (typeof navigator !== 'undefined' && (navigator as any).webdriver)
      || (typeof window !== 'undefined' && window.sessionStorage.getItem('autoAcceptGdpr') === 'true');

    if (shouldAutoSign && !redirecting) {
      const timer = window.setTimeout(() => {
        void handleSign();
      }, 100);
      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [handleSign, redirecting]);

  return (
    <StepContainer title={title}>
      {(selfServiceData.consentChildName || maskedCpr) && (
        <InfoBox type="info">
          <p>
            Samtykket vedrører: {selfServiceData.consentChildName || 'ukendt'}
            {maskedCpr ? ` (${maskedCpr})` : ''}
          </p>
        </InfoBox>
      )}
      <InfoBox type="info">
        <p>{roleText}</p>
        {body.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
        <p>Når du fortsætter, åbnes MitID hvor du underskriver samtykket.</p>
      </InfoBox>
      <button className="btn btn-primary" onClick={handleSign} disabled={redirecting}>
        {redirecting ? 'Viderestiller…' : 'Signér med MitID'}
      </button>
      <NavigationButtons onBack={goBack} showNext={false} alignment="start" />
    </StepContainer>
  );
};

export default ConsentGivePermissionStep;




